<template>
  <div v-if="item"
    class="relative w-full my-0.5 min-h-[160px] sm:min-h-[180px] md:min-h-[200px] lg:min-h-[220px] xl:min-h-[240px] 2xl:min-h-[260px] border-[2px] border-[#F8B84E] rounded-[0.313rem] overflow-hidden">
    <!-- Background image -->
    <img :src="storeData.background_file || defaultImg" @error="onImageError" alt class="absolute inset-0 w-full h-full object-cover" />
    <!-- Overlay -->
    <div class="absolute inset-0 bg-[rgba(48,48,48,0.61)]"></div>
    <!-- Ad flag -->
    <span
      class="absolute rtl:left-0 rtl:right-auto z-[10] top-1.5 py-0.5 px-1.5 lg:py-1 lg:px-2 xl:py-1.5 xl:px-2.5 left-auto right-0 bg-[#00000066] text-white text-[0.7rem] sm:text-[0.75rem] lg:text-[0.85rem] xl:text-[0.95rem] 2xl:text-[1rem] font-bold rtl:rounded-[5px_0px] rounded-[0px_5px]">
      {{ $t('general.ad') }}
    </span>
    <div class="relative w-full h-full">
      <div
        class="flex items-center flex-row flex-nowrap gap-2 sm:gap-3 md:gap-4 lg:gap-5 xl:gap-6 pt-3 pb-3 lg:pt-4 lg:pb-4 xl:pt-5 xl:pb-5 z-0 px-3 sm:px-4 lg:px-5 xl:px-6 relative w-full">
        <!-- product info (left side) -->
        <div class="flex-1 min-w-0 sm:min-w-[100px] lg:min-w-[120px] xl:min-w-[140px] flex flex-col justify-center overflow-hidden pr-1 lg:pr-2">
          <h4 class="text-[0.7rem] sm:text-[0.75rem] lg:text-[0.9rem] xl:text-[1rem] 2xl:text-[1.1rem] text-white break-words leading-tight">{{ item[nameKey] }}</h4>
          <p class="text-[0.7rem] sm:text-[0.75rem] lg:text-[0.85rem] xl:text-[0.95rem] 2xl:text-[1rem] font-normal text-white mt-1.5 sm:mt-2 lg:mt-2.5 xl:mt-3 leading-tight">
            <span v-if="item.product_type == 'retail'"> <span class="font-bold text-[11px] sm:text-[12px] lg:text-[14px] xl:text-[16px] 2xl:text-[18px]">{{
              arabizeNumbers(item.price) }}</span>ج.م</span>

            <span v-if="item.product_type === 'wholesale'">
              <span class="text-white">{{ $t('user.starts_from', {
                price:
                  arabizeNumbers(item.wholesale_details?.[0]?.price)
              }) }}</span> / <span class="text-white">{{
                  $t('user.one_piece') }}</span></span>

            <span v-if="item.product_type === 'offer'">
              {{ $t('user.offer_description_pt1', { quantity: arabizeNumbers(item.offer_details?.[0]?.quantity) }) }}
              <span class="text-primary bg-[#fdfdfd8c]"> {{ $t('user.offer_description_pt2', {
                newPrice:
                  arabizeNumbers(item.offer_details?.[0]?.new_price) }) }}</span>
              {{ $t('user.offer_description_pt3') }}
              <span class="line-through text-red-800 bg-[#fdfdfd8c]"> {{ arabizeNumbers(item.offer_details?.[0]?.old_price) }} {{
                $t('general.egy_pounds') }} </span>
            </span>
          </p>
          <div class="relative z-[5] mt-1 lg:mt-1.5 xl:mt-2 w-fit">
            <StarRating v-if="+item.ratings_avg > 0" :rating="+item.ratings_avg"
              sizesClass="w-[0.8rem] h-[0.8rem] sm:w-[0.9rem] sm:h-[0.9rem] lg:w-[1rem] lg:h-[1rem] xl:w-[1.1rem] xl:h-[1.1rem] 2xl:w-[1.2rem] 2xl:h-[1.2rem]" />
          </div>
        </div>
        <!-- product image (center) -->
        <NuxtLink 
          v-if="item.image" 
          :to="productLink" 
          class="flex-shrink-0 relative z-0 flex items-center justify-center mx-1 lg:mx-2" 
          :onclick="onClickProduct"
          @click.prevent="handleProductClick"
        >
          <div class="aspect-square w-[120px] h-[120px] sm:w-[140px] sm:h-[140px] md:w-[160px] md:h-[160px] lg:w-[180px] lg:h-[180px] xl:w-[200px] xl:h-[200px] 2xl:w-[220px] 2xl:h-[220px] rounded-[6px] border-[2px] border-primary bg-white overflow-hidden flex items-center justify-center">
            <img :src="item.image" @error="onImageError"
              class="object-contain w-full h-full"
              :width="200" :height="200" alt />
          </div>
        </NuxtLink>
        <!-- store (right side) -->
        <div v-if="storeData" class="flex-1 min-w-0 sm:min-w-[110px] md:min-w-[130px] lg:min-w-[150px] xl:min-w-[170px] 2xl:min-w-[190px] flex flex-col justify-center overflow-hidden pl-1 lg:pl-2">
          <div class="flex flex-nowrap gap-1.5 lg:gap-2 xl:gap-2.5">
            <!-- store image -->
            <div class="relative flex-shrink-0">
              <!-- verification icon -->
              <img v-if="!!storeData.verified" src="@/assets/icons/verify_white.svg"
                class="absolute top-0 rtl:right-0 w-[0.8rem] h-[0.8rem] lg:w-[1rem] lg:h-[1rem] xl:w-[1.1rem] xl:h-[1.1rem]" alt :width="13" :height="13" />
              <!-- image -->
              <NuxtLink v-if="storeData.logo" :to="storeLink" :onclick="onClickStore">
                <img :src="storeData.logo" @error="onImageError" alt :width="48" :height="48"
                  class="w-[2.5rem] h-[2.5rem] sm:w-[3rem] sm:h-[3rem] lg:w-[3.5rem] lg:h-[3.5rem] xl:w-[4rem] xl:h-[4rem] 2xl:w-[4.5rem] 2xl:h-[4.5rem] object-cover rounded-full flex-shrink-0" />
              </NuxtLink>
            </div>
            <!-- store info -->
            <div
              :class="`${(+storeData.followers_count < 1 && +storeData.ratings_avg < 1) ? 'flex flex-col justify-center' : ''} text-white font-normal overflow-visible`.trim()">
              <NuxtLink :to="storeLink" :onclick="onClickStore">
                <h3 class="text-[0.7rem] sm:text-[0.75rem] lg:text-[0.9rem] xl:text-[1rem] 2xl:text-[1.1rem] text-white line-clamp-1 break-words leading-tight"> {{ storeData.name || 'unknown' }}</h3>
              </NuxtLink>
              <span v-if="+storeData.followers_count > 0"
                class="block text-white text-[0.75rem] sm:text-[0.8rem] lg:text-[0.9rem] xl:text-[1rem] 2xl:text-[1.05rem] mt-0.5 lg:mt-1 leading-tight">{{
                  $t('general.followers_count', {
                    count:
                      arabizeNumbers(Number(storeData.followers_count).toLocaleString()) })
                }}</span>
              <div class="relative z-[5] mt-0.5 lg:mt-1 w-fit">
                <StarRating v-if="+storeData.ratings_avg > 0" :rating="+storeData.ratings_avg"
                  sizesClass="w-[0.8rem] h-[0.8rem] sm:w-[0.9rem] sm:h-[0.9rem] lg:w-[1rem] lg:h-[1rem] xl:w-[1.1rem] xl:h-[1.1rem] 2xl:w-[1.2rem] 2xl:h-[1.2rem]" />
              </div>
            </div>
          </div>
          <!-- follow/unfollow button -->
          <div class="mt-3 sm:mt-4 lg:mt-5 xl:mt-6">
            <FollowUnfollowButton appendedClass="w-full" :storeName="storeData?.name"
              :toggleFollowingState="toggleFollowingState" :followed="followed"
              :followingInProgress="followingInProgress" :designId="3" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onImageError, arabizeNumbers, buildStoreLink, buildProductLink } from '@/helpers';
import defaultImg from "@/assets/images/placeholder(doNotRemove).jpg";
import StarRating from "@/components/generic/StarRating.vue";
import FollowUnfollowButton from "@/components/user/store/FollowUnfollowButton.vue";

export default {
  data() {
    return {
      defaultImg,
      followingInProgress: false,
      followed: 0
    }
  },
  props: {
    item: {
      type: Object,
      required: true,
      default: () => ({})
    },
    saveProductCardPosition: {
      type: Function,
      required: false,
      default: null,
    },
    shouldSavePosition: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  components: {
    StarRating,
    FollowUnfollowButton
  },
  computed: {
    storeData() {
      return this.item?.store || {};
    },
    isFollowing() {
      return !!this.storeData?.following;
    },
    storeLink() {
      return buildStoreLink(this.storeData?.username);
    },
    productLink() {
      const baseLink = buildProductLink(this.item);
      
      // Check if we're on search page and include query parameters
      if (process.client) {
        const route = useRoute();
        if (route.path.includes('/search')) {
          const queryString = route.fullPath.split("?")[1];
          if (queryString) {
            return `${baseLink}?${queryString}`;
          }
        }
      }
      
      return baseLink;
    }

  },
  methods: {
    arabizeNumbers,
    onImageError,
    async onClickStore(){
        const model = 'store_product';
        const id = this.item.id;

        await this.makeRequest({
          url: `${this.APIREQUESTS.user.recordClick.url}/${model}/${id}`,
          method: this.APIREQUESTS.user.recordClick.method,
          includeUID: true
        }).then((res) => {
          if (!res?.status) {
          } else {
            // success
          }
        })
        .catch((err) => {
          //rejected
        });
      },
    async onClickProduct(){
        const model = 'store_product';
        const id = this.item.id;

        await this.makeRequest({
          url: `${this.APIREQUESTS.user.recordClick.url}/${model}/${id}`,
          method: this.APIREQUESTS.user.recordClick.method,
          includeUID: true
        }).then((res) => {
          if (!res?.status) {
          } else {
            // success
          }
        })
        .catch((err) => {
          //rejected
        });
      },
    setTheFollowingState(isFollowed) {
      this.followed = +isFollowed;
    },
    toggleFollowingState() {
      const isOffline = !navigator.onLine;
      if (this.followingInProgress || !this.storeData.id || isOffline) {
        return;
      }
      this.followingInProgress = true;

      if (this.isUserLoggedIn) {
        this.setTheFollowingState(!this.followed);
      }

      const bodyRequest = {
        store_id: this.storeData.id
      };

      this.makeRequest({
        owner: this.MAINROLES.user,
        url: this.APIREQUESTS.user.followStore.url,
        method: this.APIREQUESTS.user.followStore.method,
        formData: JSON.stringify(bodyRequest)
      })
        .then(res => {
          if (!res?.status) {
            this.setTheFollowingState(this.isFollowing);
            // fail
            this.notify({
              type: "error",
              msg: res?.message || this.$t("general.failed")
            });
          } else {
            // success
            this.setTheFollowingState(+res.data?.following);

            const storeUsername = this.storeUsername;
            let tempStoreData = this.savedStoresData?.[storeUsername]
            if (tempStoreData?.data?.store) {
              tempStoreData.data.store.following = +res.data?.following;
            }
          }
        })
        .catch(err => {
          // fail
          this.setTheFollowingState(this.isFollowing);
          // console.error(err);
          this.notify({
            type: "error",
            msg:
              (typeof err === "string" ? err : err?.data?.message) ||
              this.$t("general.failed")
          });
        })
        .finally(() => {
          this.followingInProgress = false;
        });
    },
    async handleProductClick(event) {
      // Call onClickProduct for ad tracking (don't await, fire and forget)
      if (this.onClickProduct) {
        this.onClickProduct();
      }
      
      // If should save position and handler provided
      if (this.shouldSavePosition && this.saveProductCardPosition) {
        try {
          // Call save handler (this should return a Promise)
          const saveResult = this.saveProductCardPosition(this.item.id);
          
          // Navigate after save completes
          if (saveResult instanceof Promise) {
            await saveResult;
            this.$router.push(this.productLink);
          } else {
            // Small delay to ensure save completes if not a Promise
            setTimeout(() => {
              this.$router.push(this.productLink);
            }, 50);
          }
        } catch (error) {
          // Navigate even if save fails
          this.$router.push(this.productLink);
        }
      } else {
        // Normal navigation if no save handler
        this.$router.push(this.productLink);
      }
    },
  },
  mounted() {
    this.setTheFollowingState(!!this.isFollowing);
  }
}
</script>

<style></style>